import { z } from "zod";

export enum ContactInquiryType {
  Executive = 1,
  Architecture = 2,
  Solution = 3,
  Partnership = 4,
  Developer = 5,
  General = 6,
}
export enum ContactLocationScale {
  Small = 1,
  Medium = 2,
  Large = 3,
  Enterprise = 4,
}
const APP_LOCALES = ["en", "vi", "ja", "zh", "ko"] as const;
const PHONE_PATTERN = /^\+?[\d\s().\-/]{7,24}$/;

export type ContactFormMessages = {
  required: string;
  email: string;
  phone: string;
  messageMax: string;
  nameMax: string;
  companyMax: string;
  businessAddressMax: string;
};

export function createContactFormSchema(messages: ContactFormMessages) {
  return z.object({
    firstName: z
      .string()
      .trim()
      .min(1, { message: messages.required })
      .max(100, { message: messages.nameMax }),
    lastName: z
      .string()
      .trim()
      .min(1, { message: messages.required })
      .max(100, { message: messages.nameMax }),
    email: z
      .string()
      .trim()
      .min(1, { message: messages.required })
      .max(320)
      .email({ message: messages.email }),
    phone: z
      .string()
      .trim()
      .superRefine((val, ctx) => {
        if (!val) return;
        if (!PHONE_PATTERN.test(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: messages.phone,
          });
        }
      }),
    company: z
      .string()
      .trim()
      .min(1, { message: messages.required })
      .max(200, { message: messages.companyMax }),
    businessAddress: z
      .string()
      .trim()
      .min(1, { message: messages.required })
      .max(255, { message: messages.businessAddressMax }),
    locationScale: z.enum(ContactLocationScale),
    inquiryType: z.enum(ContactInquiryType),
    message: z.string().trim().max(4000, { message: messages.messageMax }),
  });
}

export const contactFormApiSchema = createContactFormSchema({
  required: "Required",
  email: "Invalid email",
  phone: "Invalid phone",
  messageMax: "Message too long",
  nameMax: "Name too long",
  companyMax: "Company name too long",
  businessAddressMax: "Business address too long",
}).extend({
  locale: z.enum(APP_LOCALES).optional(),
});

export type ContactFormPayload = z.infer<typeof contactFormApiSchema>;
export type ContactFormFieldValues = Omit<ContactFormPayload, "locale">;
